import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { listWeeklyNoShows, createMMUpdate } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertTriangle, Phone, CalendarX, Send, CheckCircle2 } from "lucide-react";

const REACHOUT_OPTIONS = ["Call", "WhatsApp", "In-person", "Email"];

export default function NoShowFollowUps() {
  const { activeFacility } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  // Reached out = a Member Manager update saved for them this week (CR-0064), so it survives a refresh
  // and every MM sees it before calling.
  const [view, setView] = useState("to_call");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await listWeeklyNoShows());
    } catch {
      toast.error("Could not load weekly no-shows");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load, activeFacility?.id]);

  const noShows = data?.no_shows || [];
  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const inView = noShows.filter((c) => view === "all" || (view === "reached" ? !!c.reached_out : !c.reached_out));
    if (!q) return inView;
    return inView.filter((c) => `${c.name || ""} ${c.phone || ""} ${c.member_manager || ""}`.toLowerCase().includes(q));
  }, [noShows, filter, view]);
  const reachedCount = data?.reached_out_count ?? 0;

  return (
    <div className="space-y-6" data-testid="no-shows-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow">Retention · Weekly follow-ups</div>
          <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight">No-Show Follow-ups</h2>
          <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
            Active members who haven&apos;t attended a single session so far this week. Every row needs a Member Manager call and a logged update.
          </p>
        </div>
        <Button variant="outline" onClick={load} data-testid="refresh-no-shows">Refresh</Button>
      </div>

      {/* Banner — only from Wed EOD onwards */}
      {data?.show_follow_up_banner && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-500/30 bg-amber-500/[0.07] px-4 py-3 text-sm text-amber-200" data-testid="follow-up-banner">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <div className="font-medium">Mon → Wed window closed — call them today.</div>
            <div className="mt-0.5 text-xs text-amber-300/70">
              It&apos;s past Wednesday and these members haven&apos;t shown up yet this week. Log each call under &quot;Record follow-up&quot; so everyone can see they&apos;ve been reached out to.
            </div>
          </div>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="no-shows-kpis">
        <Kpi label="Week" value={data ? `${fmtShort(data.week_start)} → ${fmtShort(data.week_end)}` : "—"} />
        <Kpi label="Attended this week" value={data?.attended_count ?? "—"} tint="emerald" />
        <Kpi label="No-shows" value={data?.no_show_count ?? "—"} tint="rose" />
        <Kpi label="Reached out this week" value={data ? reachedCount : "—"} tint="amber" />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Input
          data-testid="filter-no-shows"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by name, phone, or manager…"
          className="max-w-sm"
        />
        <div className="flex rounded-md border border-[#262626] text-xs" data-testid="no-shows-view">
          {[["to_call", `To call (${noShows.length - reachedCount})`], ["reached", `Reached out (${reachedCount})`], ["all", "All"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setView(k)} data-testid={`no-shows-view-${k}`}
              className={`px-3 py-1.5 ${view === k ? "bg-[#1a1a1a] text-zinc-100" : "text-zinc-400 hover:text-zinc-200"}`}>{l}</button>
          ))}
        </div>
        <span className="text-xs text-zinc-500" data-testid="rows-shown">{filtered.length} rows</span>
      </div>

      {loading ? (
        <div className="card-surface p-8 text-center text-sm text-zinc-500">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="card-surface p-10 text-center text-sm text-zinc-500" data-testid="no-shows-empty">
          <CheckCircle2 className="mx-auto mb-3 h-6 w-6 text-emerald-400" />
          {view === "to_call" && noShows.length ? "Everyone on the list has been reached out to this week." : view === "reached" ? "No one reached out to yet this week." : "Everyone active has checked in this week. Nice."}
        </div>
      ) : (
        <div className="space-y-2" data-testid="no-shows-list">
          {(filtered || []).map((c) => (
            <NoShowRow
              key={c.customer_key}
              row={c}
              done={!!c.reached_out}
              onLogged={load}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function NoShowRow({ row, done, onLogged }) {
  const [expanded, setExpanded] = useState(false);
  const [reachoutType, setReachoutType] = useState("Call");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const log = async () => {
    if (!note.trim()) { toast.error("Add a short note so the trail is useful"); return; }
    setSaving(true);
    try {
      await createMMUpdate({
        customer_key: row.customer_key,
        customer_name: row.name,
        member_manager: row.member_manager || undefined,
        reachout_type: reachoutType,
        note: note.trim(),
        source: "no_show",
      });
      toast.success(`Follow-up logged for ${row.name}`);
      onLogged();
      setExpanded(false);
      setNote("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not log follow-up");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`card-surface overflow-hidden transition-colors ${done ? "border-emerald-500/30" : ""}`} data-testid={`no-show-row-${row.customer_key}`}>
      <div className="flex flex-wrap items-center gap-3 px-4 py-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <CalendarX className="h-3.5 w-3.5 text-rose-400" />
            <div className="font-medium text-zinc-100">{row.name}</div>
            {done && <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-emerald-300">Reached out</span>}
          </div>
          <div className="text-[11px] text-zinc-500">
            {row.phone || "no phone"}
            {row.programme ? ` · ${row.programme}` : ""}
            {row.end_date ? ` · ends ${row.end_date}` : ""}
            {row.member_manager ? ` · MM: ${row.member_manager}` : " · MM: unassigned"}
          </div>
          {row.reached_out && (
            <div className="mt-1 text-[11px] text-emerald-300/80" data-testid={`reached-out-${row.customer_key}`}>
              {row.reached_out.by || "Someone"} · {fmtShort(row.reached_out.date)}{row.reached_out.reachout_type ? ` · ${row.reached_out.reachout_type}` : ""}
              {row.reached_out.note ? `: ${row.reached_out.note}` : ""}
            </div>
          )}
        </div>
        {row.phone && (
          <a href={`tel:${row.phone}`} className="rounded-md border border-[#262626] px-2.5 py-1.5 text-xs text-zinc-300 hover:border-emerald-500/50" data-testid={`call-${row.customer_key}`}>
            <Phone className="mr-1 inline h-3 w-3" /> Call
          </a>
        )}
        <Button
          variant={expanded ? "ghost" : "default"}
          size="sm"
          data-testid={`toggle-followup-${row.customer_key}`}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? "Close" : done ? "Log another" : "Record follow-up"}
        </Button>
      </div>
      {expanded && (
        <div className="grid gap-3 border-t border-[#1f1f1f] bg-[#0a0a0a] px-4 py-3 md:grid-cols-[160px_1fr_auto]" data-testid={`followup-form-${row.customer_key}`}>
          <div>
            <div className="mb-1 text-[10px] uppercase tracking-[0.18em] text-zinc-500">Reach-out</div>
            <select
              data-testid={`followup-type-${row.customer_key}`}
              value={reachoutType}
              onChange={(e) => setReachoutType(e.target.value)}
              className="w-full rounded-md border border-input bg-[#0d0d0d] px-3 py-2 text-sm text-zinc-100"
            >
              {REACHOUT_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <div className="mb-1 text-[10px] uppercase tracking-[0.18em] text-zinc-500">Reason / note *</div>
            <Input
              data-testid={`followup-note-${row.customer_key}`}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. travelling — back Thursday; sore knee, resuming Monday"
            />
          </div>
          <div className="flex items-end">
            <Button data-testid={`followup-save-${row.customer_key}`} onClick={log} disabled={saving}>
              <Send className="mr-1.5 h-3.5 w-3.5" /> {saving ? "Logging…" : "Log"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, tint }) {
  const tintCls = tint === "emerald" ? "text-emerald-300"
    : tint === "rose" ? "text-rose-300"
    : tint === "amber" ? "text-amber-300" : "text-zinc-100";
  return (
    <div className="card-surface p-4">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <div className={`font-display mt-1 text-xl font-extrabold tabular-nums ${tintCls}`}>{value}</div>
    </div>
  );
}

const fmtShort = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
};
